import type {
  LensDatasetCardDiff as DatasetCardDiff,
  LensDatasetExtendCheckResponse as DatasetCheckResult,
} from '~/hooks/apis/reporting/useLeLensDatasetChecks';

import type { LensCard, LensDataset } from '~/types/leLens';

import { formatLensValue } from '../../lensChart';
import { formatBytes } from '../panelFormat';

/**
 * Turns an extend-check or merge-check response into the exact copy
 * 02_RIGHT_PANEL_SPEC.md specifies for the guard result block, and the
 * decision of which state to show.
 *
 * "Identical" is read off `cardDiffs[].identical` (server-side, computed by
 * shaping every dependent card's view against the old and new rows with the
 * same `applyView` the browser uses), not off `grainKept`/`removed` alone:
 * those are supporting facts for the copy, but whether a reader's numbers
 * move is the only thing that decides pass or refuse, per the plan's own
 * rule ("means what a reader would actually see, not just that the SQL
 * parses").  A removed column is refused outright even when today's cards
 * happen not to read it, since a card added tomorrow would silently break.
 */

export type AddedColumn = { name: string; type: string };

export type ChangedCard = { cardId: string; title: string; display: string };

export type GuardPass = {
  kind: 'pass';
  /** Columns the candidate SQL returns that the dataset does not today, typed via a
   *  separate free dry run of the same SQL (the check response itself only names them). */
  addedColumns: Array<AddedColumn>;
  keptCount: number;
  totalCount: number;
  /** "Grain unchanged: order_date × website_region × product" or "Grain not verified on this run". */
  grainLine: string;
  cardCount: number;
  bytesLine: string;
};

export type GuardRefuse = {
  kind: 'refuse';
  /** "This would add hotel_type to the grain." / "This would stop returning refund_aud." / a generic fallback. */
  reasonLine: string;
  changedCards: Array<ChangedCard>;
  removedColumns: Array<string>;
  /**
   * D7: "Apply anyway" overrides a grain-widening refusal only. It is not offered when a
   * column was dropped or retyped (`removed`/`typeChanges` non-empty): the backend's own
   * schema validation (`unresolvableViewColumn`) refuses that save unconditionally, since a
   * card still names a column the rewrite no longer returns, so offering the override would
   * only fail on Apply. True exactly when this refusal is a pure grain change.
   */
  canApplyAnyway: boolean;
};

export type GuardOutcome = GuardPass | GuardRefuse;

/** The one non-key numeric value on a shaped row: the measure a grouped or metric result carries. */
function firstNumericValue(row: Record<string, unknown> | undefined): { column: string; value: number } | undefined {
  if (!row) {
    return undefined;
  }
  for (const [column, value] of Object.entries(row)) {
    // A dropped column's value arrives here as `null` (a real, live-verified case: extend-check's
    // "removed" branch shapes the new rows against a card whose view still names the column that
    // no longer exists, and it comes back null). `Number(null)` is 0, not NaN, so it has to be
    // excluded before the coercion below or a dropped measure reads as "the total became $0"
    // instead of "this column is gone".
    if (value === null || value === undefined || typeof value === 'boolean' || String(value).trim() === '') {
      continue;
    }
    const numeric = typeof value === 'number' ? value : Number(value);
    if (Number.isFinite(numeric)) {
      return { column, value: numeric };
    }
  }
  return undefined;
}

function isShapedResult(value: unknown): value is { rows: Array<Record<string, unknown>>; columns: Array<string> } {
  return (
    Boolean(value) &&
    typeof value === 'object' &&
    Array.isArray((value as { rows?: unknown }).rows) &&
    Array.isArray((value as { columns?: unknown }).columns)
  );
}

/** A short, honest summary of what changed for one card: a single before/after number when the
 *  shaped result is one row (a metric, or any card grouped down to a single group), and a plain
 *  row-count statement otherwise, rather than blending several categories into one synthetic
 *  number nobody asked for. */
export function describeCardChange(
  diff: DatasetCardDiff,
  card: LensCard | undefined,
  dataset: LensDataset | undefined,
) {
  const format =
    dataset?.columns?.find((column) => column.name === card?.view?.value && column.kind === 'calculated')?.format ??
    card?.config?.format;

  if (
    isShapedResult(diff.before) &&
    isShapedResult(diff.after) &&
    diff.before.rows.length === 1 &&
    diff.after.rows.length === 1
  ) {
    const before = firstNumericValue(diff.before.rows[0]);
    const after = firstNumericValue(diff.after.rows[0]);
    if (before && after) {
      return `${formatLensValue(before.value, format)} to ${formatLensValue(after.value, format)}`;
    }
    // Live-verified case: dropping a column the card reads leaves its value `null`, not a real
    // number, on one side only. Say so plainly rather than falling through to a row count that
    // reads as though nothing about the shape changed.
    if (before && !after) {
      return `${formatLensValue(before.value, format)} to no longer returned`;
    }
    if (after && !before) {
      return `not returned before to ${formatLensValue(after.value, format)}`;
    }
  }
  if (isShapedResult(diff.before) && isShapedResult(diff.after)) {
    return `${diff.before.rows.length} rows to ${diff.after.rows.length} rows`;
  }
  return 'the rows returned differ';
}

export function guardOutcome(
  result: DatasetCheckResult,
  dryRunColumns: ReadonlyArray<{ name: string; type: string }>,
  dataset: LensDataset,
  cards: ReadonlyArray<LensCard>,
): GuardOutcome {
  const currentNames = new Set((dataset.columns ?? []).map((column) => column.name));
  const addedColumns = dryRunColumns.filter((column) => !currentNames.has(column.name));
  const nonIdentical = result.cardDiffs.filter((diff) => !diff.identical);
  const dependentCount = result.cardDiffs.length;

  if (result.removed.length || nonIdentical.length) {
    const cardById = new Map(cards.map((card) => [card.id, card]));
    const changedCards = nonIdentical.map((diff) => ({
      cardId: diff.cardId,
      title: cardById.get(diff.cardId)?.title ?? diff.cardId,
      display: describeCardChange(diff, cardById.get(diff.cardId), dataset),
    }));
    const reasonLine = result.removed.length
      ? `This would stop returning ${result.removed.join(', ')}.`
      : !result.grainKept
        ? `This would add ${addedColumns.map((column) => column.name).join(', ') || 'a new column'} to the grain.`
        : 'This would change what some cards show.';
    const canApplyAnyway = !result.removed.length && !result.typeChanges.length && !result.grainKept;
    return { canApplyAnyway, changedCards, kind: 'refuse', reasonLine, removedColumns: result.removed };
  }

  const keptCount = (dataset.columns ?? []).length - result.removed.length;
  const totalCount = (dataset.columns ?? []).length;
  const grainLine = result.grainKept
    ? `Grain unchanged${dataset.grain?.keys?.length ? `: ${dataset.grain.keys.join(' × ')}` : ''}.`
    : 'Grain not verified on this run.';

  return {
    addedColumns,
    bytesLine: `1 dry run · ${formatBytes(result.bytesProcessed)} scanned`,
    cardCount: dependentCount,
    grainLine,
    keptCount,
    kind: 'pass',
    totalCount,
  };
}

/** "+refund_aud NUMERIC. 8 of 8 columns kept. Grain unchanged. 4 cards re-run: identical. 14 MB scanned." */
export function passSummaryLine(outcome: GuardPass): string {
  const added = outcome.addedColumns.map((column) => `+${column.name} ${column.type}`).join(', ');
  const cardsLine = outcome.cardCount ? `${outcome.cardCount} cards re-run: identical.` : 'No cards read this yet.';
  return [
    added ? `${added}.` : undefined,
    `${outcome.keptCount} of ${outcome.totalCount} columns kept.`,
    outcome.grainLine,
    cardsLine,
  ]
    .filter(Boolean)
    .join(' ');
}

/** "{a} and {b} both read {table} at {grain}. Merge them?" */
export function mergeBannerLine(
  a: LensDataset,
  b: LensDataset,
  table: string,
  grainKeys: ReadonlyArray<string>,
): string {
  return `${a.name} and ${b.name} both read ${table} at ${grainKeys.join(' × ')}. Merge them?`;
}

/**
 * F7's exact wording (01_USER_FLOWS.md) for a card the merge preview found non-identical: it
 * stays on its own dataset rather than moving, since moving it would change its number. Kept as
 * its own function here rather than reused from `combineQueries.ts`'s `describeKeptReason`,
 * which carries the legacy SQL-text merge flow's own plural wording for a different UI
 * (`DatasetsDialog.tsx`) and must not change to match this one.
 */
export function blockedCardReason(): string {
  return 'Kept on its own dataset because its number would change.';
}
